import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { NotificationContainer } from '../components/NotificationToast';

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const formatAmount = (amount) => `${new Intl.NumberFormat('en-US').format(amount ?? 0)} TND`;

  const addNotification = useCallback((notification) => {
    const id = Date.now() + Math.random();
    const newNotification = {
      id,
      timestamp: new Date(),
      duration: 5000,
      ...notification
    };

    setNotifications(prev => [...prev, newNotification]);

    // Auto-remove after duration
    setTimeout(() => {
      removeNotification(id);
    }, newNotification.duration);

    return id;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const removeNotification = useCallback((id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  const clearAll = useCallback(() => {
    setNotifications([]);
  }, []);

  // Specific notification types for second-hand offers
  const showOfferNotification = useCallback((type, data) => {
    const notificationConfigs = {
      'new_offer': {
        title: 'New Offer Received!',
        message: `${data.buyerName} made an offer of ${formatAmount(data.amount)} on "${data.productName}"`,
        type: 'new_offer',
        action: data.action ? {
          label: 'View Offer',
          onClick: data.action
        } : null
      },
      'offer_accepted': {
        title: 'Offer Accepted!',
        message: `Your offer of ${formatAmount(data.amount)} on "${data.productName}" has been accepted!`,
        type: 'offer_accepted',
        action: data.action ? {
          label: 'Open Chat',
          onClick: data.action
        } : null
      },
      'offer_rejected': {
        title: 'Offer Rejected',
        message: `Your offer on "${data.productName}" has been rejected`,
        type: 'offer_rejected'
      },
      'fee_payment_required': {
        title: 'Payment Required',
        message: `Pay ${formatAmount(data.amount)} to start chatting with the seller`,
        type: 'fee_payment_required',
        action: data.action ? {
          label: 'Pay Now',
          onClick: data.action
        } : null
      },
      'fee_paid': {
        title: 'Chat Unlocked!',
        message: `You can now chat with the ${data.userType} about "${data.productName}"`,
        type: 'chat_unlocked',
        action: data.action ? {
          label: 'Start Chat',
          onClick: data.action
        } : null
      },
      'chat_message': {
        title: 'New Message',
        message: `${data.senderName}: ${data.message}`,
        type: 'chat_message',
        action: data.action ? {
          label: 'View Chat',
          onClick: data.action
        } : null
      },
      'offer_withdrawn': {
        title: 'Offer Withdrawn',
        message: `Offer on "${data.productName}" has been withdrawn`,
        type: 'offer_withdrawn'
      },
      'offer_expired': {
        title: 'Offer Expired',
        message: `Your offer on "${data.productName}" has expired`,
        type: 'offer_expired'
      }
    };

    const config = notificationConfigs[type];
    if (config) {
      addNotification(config);
    }
  }, [addNotification]);

  const showSuccess = useCallback((message, action = null) => {
    addNotification({
      title: 'Success',
      message,
      type: 'success',
      action
    });
  }, [addNotification]);

  const showError = useCallback((message, action = null) => {
    addNotification({
      title: 'Error',
      message,
      type: 'error',
      action,
      duration: 7000 // Errors stay longer
    });
  }, [addNotification]);

  const showWarning = useCallback((message, action = null) => {
    addNotification({
      title: 'Warning',
      message,
      type: 'warning',
      action
    });
  }, [addNotification]);

  const showInfo = useCallback((message, action = null) => {
    addNotification({
      title: 'Info',
      message,
      type: 'info',
      action
    });
  }, [addNotification]);

  const value = {
    notifications,
    addNotification,
    removeNotification,
    clearAll,
    showOfferNotification,
    showSuccess,
    showError,
    showWarning,
    showInfo
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <NotificationContainer 
        notifications={notifications}
        onRemoveNotification={removeNotification}
      />
    </NotificationContext.Provider>
  );
};
